import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { api } from '../Api';
import { Store } from '../Store';

// Which database is under this admin panel.
//
// The testing site and the live site look identical, and the testing database
// is NOT sanitised — an OTP triggered there goes to a real phone and spends
// real gateway credit. This badge exists so nobody has to remember which tab
// they are in before pressing something that costs money.
//
// Yellow means testing. Green means live. The colours are the wrong way round
// from a traffic light on purpose: green is not "safe to experiment", it is
// "these are real customers", and yellow is the one that says look twice.
// The wording carries that, because colour alone would be ambiguous — and
// would say nothing at all to an admin who cannot distinguish the two.
// `detailed` adds the server and database underneath, for the developer
// section: the badge answers "is this live?", the detail answers "live WHERE?".
// Only ever hostnames and a database name — never a user, a password or a
// connection string.
export default function DbEnvironmentBadge({ compact = false, detailed = false }) {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(api + '/api/developer/environment', {
          headers: { Authorization: `Bearer ${userInfo?.token}` },
        });
        if (active) setInfo(data);
      } catch {
        /* non-critical widget — a failure here must not break the sidebar */
      }
    })();
    return () => { active = false; };
  }, [userInfo]);

  if (!info) return null;

  const testing = info.environment === 'testing';
  const palette = testing
    ? { bg: '#fef9c3', border: '#fde047', text: '#854d0e' }
    : { bg: '#dcfce7', border: '#a7f3d0', text: '#065f46' };

  // A database whose name gave no clue was assumed to be live. Saying so is
  // better than a confident green that happens to be wrong.
  const label = testing ? 'TESTING DATABASE' : 'LIVE DATABASE';
  const title = testing
    ? `Connected to ${info.dbName} — a testing database. It is not sanitised: OTPs sent from here reach real phones and spend real credit.`
    : `Connected to ${info.dbName} — the live database.${info.inferred ? ' (Assumed live: the name did not identify it. Set APP_ENV to be sure.)' : ''}`;

  const base = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 10,
    border: `1px solid ${palette.border}`,
    background: palette.bg,
    color: palette.text,
    fontWeight: 800,
    letterSpacing: 0.3,
  };

  if (compact) {
    return (
      <div
        title={title}
        style={{
          ...base,
          width: '90%',
          margin: '10px auto 0',
          padding: '7px 12px',
          fontSize: 11,
          justifyContent: 'space-between',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: palette.text, flexShrink: 0,
            // A connection that has dropped is not the same as a healthy one,
            // and the dot is the cheapest place to say so.
            opacity: info.connected ? 1 : 0.35,
          }}
          />
          {label}
        </span>
        <span style={{ fontWeight: 600, opacity: 0.75, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
          {info.dbName}
        </span>
      </div>
    );
  }

  const badge = (
    <div title={title} style={{ ...base, padding: '9px 14px', fontSize: 12, display: 'inline-flex' }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: palette.text, opacity: info.connected ? 1 : 0.35 }} />
      {label}
      <span style={{ fontWeight: 600, opacity: 0.75 }}>· {info.dbName}</span>
      {!info.connected ? <span style={{ fontWeight: 600 }}>· disconnected</span> : null}
    </div>
  );

  if (!detailed) return badge;

  const rows = [
    // The cluster is the name from the connection string; the server is the
    // machine actually answering. On a replica set those differ, and the gap
    // between them is worth seeing.
    ['Cluster', info.cluster || '—'],
    ['Server', info.host ? `${info.host}${info.port ? `:${info.port}` : ''}` : '—'],
    ['Database', info.dbName || '—'],
    ['Connection', info.connected ? (info.srv ? 'connected (mongodb+srv)' : 'connected') : 'NOT CONNECTED'],
  ];

  return (
    <div>
      {badge}
      <div style={{
        marginTop: 8,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        background: '#fff',
        padding: '8px 12px',
        fontSize: 12,
        display: 'inline-block',
        minWidth: 280,
      }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 10, padding: '2px 0' }}>
            <span style={{ opacity: 0.6, minWidth: 78 }}>{k}</span>
            <strong style={{ wordBreak: 'break-all', fontWeight: 600 }}>{v}</strong>
          </div>
        ))}
        {info.inferred ? (
          <div style={{ marginTop: 4, opacity: 0.7 }}>
            Environment assumed from the database name. Set APP_ENV to be certain.
          </div>
        ) : null}
      </div>
    </div>
  );
}
